import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import NewNavbar from "../components/NewNavbar.js";
import SEO from "../components/SEO.js";
import QuickPayButton from "../components/QuickPayButton.js";
import {
  decrementQuantity,
  getCartProductAvailableQuantity,
  incrementQuantity,
  isCollectionCartProduct,
  removeFromCart,
  selectCartItems,
  selectHasInvalidStockQuantity,
  groupCartItems,
  removeBundleFromCart
} from "../store/cartSlice";
import { useProfileReadQuery } from "../store/api.ts";
import toast from "react-hot-toast";
import AuthModal from "../components/AuthModal.js";
import RootPersonalizationModal from "../components/RootPersonalizedModal.js";
import Footer from "../components/Footer.js";
import {
  formatStorePrice,
  getPendingSurveyToken,
  getProductPricing,
} from "../utils.js";
import HomeProfilePopupFlow from "../components/HomeProfilePopupFlow.js";
import CartBundleCard from "../components/CartBundleCard";
import { getCartBundlePricing } from "../utils/cartBundle";

const CartRow = ({ item, onViewProduct }) => {
  const dispatch = useDispatch();

  const product = item?.product || {};
  const quantity = Number(item?.quantity || 1);

  const isCollectionProduct = isCollectionCartProduct(product);

  const availableQuantity = getCartProductAvailableQuantity(product);
  const isExclusiveProduct = product?.productType === "exclusive" || product?.product_type === "exclusive";

  const hasKnownStock =
    isCollectionProduct &&
    availableQuantity !== null;

  const isOutOfStock =
    hasKnownStock &&
    availableQuantity <= 0;

  const hasReachedStockLimit =
    hasKnownStock &&
    quantity >= availableQuantity;

  const isQuantityAboveStock =
    hasKnownStock &&
    quantity > availableQuantity;

  const pricing = getProductPricing(
    product,
    isCollectionProduct,
  );

  const originalUnitPrice = pricing.originalPrice;
  const discountedUnitPrice = pricing.discountedPrice;

  const originalLineTotal =
    originalUnitPrice * quantity;

  const discountedLineTotal =
    discountedUnitPrice * quantity;

  const lineSavings = Math.max(
    originalLineTotal - discountedLineTotal,
    0,
  );

  const discountPercentage = Number(
    pricing.discountPercent.toFixed(2),
  );

  const imageFromMainImages = Array.isArray(product?.main_images)
    ? product.main_images.find(
        (src) =>
          typeof src === "string" &&
          src.trim().length > 0 &&
          src !== "null" &&
          !src.endsWith("/null"),
      )
    : "";

  const imageSrc =
    imageFromMainImages ||
    product?.image ||
    product?.image_url ||
    "";

  const handleIncrementQuantity = () => {
    if (isOutOfStock) {
      toast.error(
        `${product?.size || "This size"} is out of stock.`,
      );
      return;
    }

    if (hasReachedStockLimit) {
      toast.error(
        isExclusiveProduct
          ? "Maximum quantity reached for this size."
          : availableQuantity === 1
            ? "Only 1 item is available for this size."
            : `Only ${availableQuantity} items are available for this size.`,
      );
      return;
    }

    dispatch(incrementQuantity(product?.id));
  };

  return (
    <div className="flex min-h-[122px] w-full items-center justify-between rounded-[6px] border border-[#DFDFDF] bg-white px-5 py-3">
      <div className="flex min-w-0 items-center gap-4">
        <div className="h-[70px] w-[70px] shrink-0 overflow-hidden rounded-[4px] bg-[#D6D6D6]">
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={product?.name || "Product"}
              className="h-full w-full object-cover"
            />
          ) : null}
        </div>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-[14px] font-medium text-black">
              {product?.name || "Product"}
            </div>
            {pricing.hasDiscount ? (
              <span className="rounded-[4px] bg-[#EAF7EE] px-2 py-1 font-spaceMono text-[10px] font-semibold text-[#257A45]">
                {discountPercentage}% OFF
              </span>
            ) : null}
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <span className="h-[26px] rounded-[4px] bg-[#E7F0FF] px-2 py-1 font-spaceMono text-[12px] font-normal text-[#3D648B]">
              Size: {product?.size || "M"}
            </span>
            <span className="h-[26px] rounded-[4px] bg-[#E7F0FF] px-2 py-1 font-spaceMono text-[12px] font-normal text-[#3D648B]">
              Colour: {product?.color || "Blue"}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px]">
            <span className="text-[#7B7B87]">
              Unit Price:
            </span>
            {pricing.hasDiscount ? (
              <span className="text-[#8A8A8A] line-through">
                {formatStorePrice(originalUnitPrice)}
              </span>
            ) : null}
            <span
              className={
                pricing.hasDiscount
                  ? "font-semibold text-[#257A45]"
                  : "font-medium text-black"
              }
            >
              {formatStorePrice(discountedUnitPrice)}
            </span>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <div className="flex h-[26px] w-[134px] items-center justify-between rounded-[4px] border-[1px] bg-[#313131] px-3 text-white">
              <button
                type="button"
                onClick={() => dispatch(decrementQuantity(product?.id))}
                disabled={quantity <= 1}
                className="text-[18px] leading-none disabled:cursor-not-allowed disabled:opacity-40"
              >
                -
              </button>

              <span className="font-spaceMono text-[14px] font-normal">
                {quantity}
              </span>

              <button
                type="button"
                onClick={handleIncrementQuantity}
                disabled={
                  isOutOfStock ||
                  hasReachedStockLimit
                }
                aria-label={
                  isOutOfStock
                    ? "Product is out of stock"
                    : hasReachedStockLimit
                      ? isExclusiveProduct
                        ? "Maximum quantity reached"
                        : `Maximum available quantity is ${availableQuantity}`
                      : "Increase quantity"
                }
                title={
                  isOutOfStock
                    ? "Out of stock"
                    : hasReachedStockLimit
                      ? isExclusiveProduct
                        ? "Maximum quantity reached"
                        : `Only ${availableQuantity} available`
                      : undefined
                }
                className={[
                  "text-[18px] leading-none",
                  isOutOfStock ||
                  hasReachedStockLimit
                    ? "cursor-not-allowed opacity-40"
                    : "",
                ].join(" ")}
              >
                +
              </button>
            </div>

            {hasKnownStock &&
            (!isExclusiveProduct ||
              isOutOfStock ||
              hasReachedStockLimit) ? (
              <span
                className={[
                  "font-spaceMono text-[10px] font-medium",
                  isOutOfStock
                    ? "text-[#B42318]"
                    : hasReachedStockLimit
                      ? "text-[#B54708]"
                      : "text-[#667085]",
                ].join(" ")}
              >
                {isOutOfStock
                  ? "Out of stock"
                  : hasReachedStockLimit
                    ? isExclusiveProduct
                      ? "Maximum quantity reached"
                      : `Maximum ${availableQuantity}`
                    : `${availableQuantity} available`}
              </span>
            ) : null}

            <button
              type="button"
              onClick={() => dispatch(removeFromCart(product?.id))}
              className="text-[12px] font-medium text-[#C45A4E] underline underline-offset-2 transition hover:opacity-80"
            >
              Remove Item
            </button>
            {isQuantityAboveStock ? (
              <p className="w-full font-spaceMono text-[10px] font-medium text-[#B42318]">
                {isExclusiveProduct
                  ? "Selected quantity exceeds available stock. Please reduce the quantity."
                  : `Only ${availableQuantity} available. Please reduce the quantity.`}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex h-full min-w-[140px] flex-col items-end justify-between gap-6">
        <button
          type="button"
          onClick={() => onViewProduct?.(product)}
          className="text-[11px] font-normal text-[#595973] hover:underline"
        >
          View Product
        </button>

        <div className="mt-[18px] text-right">
          {pricing.hasDiscount ? (
            <div className="text-[13px] font-normal text-[#8A8A8A] line-through">
              {formatStorePrice(originalLineTotal)}
            </div>
          ) : null}

          <div
            className={[
              "text-[20px] font-medium",
              pricing.hasDiscount
                ? "text-[#257A45]"
                : "text-black",
            ].join(" ")}
          >
            {formatStorePrice(discountedLineTotal)}
          </div>

          {lineSavings > 0 ? (
            <div className="mt-1 text-[11px] font-medium text-[#257A45]">
              Saved {formatStorePrice(lineSavings)}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const EmptyCartState = () => {
  return (
    <div className="rounded-[10px] border border-[#E9E9EE] bg-white px-6 py-10 text-center text-[#7B7B87]">
      Your cart is empty.
    </div>
  );
};

const AccessMessage = ({ children }) => {
  return (
    <div className="mt-8 rounded-[10px] border border-[#E9E9EE] bg-white px-6 py-10 text-center text-[#7B7B87]">
      {children}
    </div>
  );
};

const NewCartPage = () => {
  const cartItems = useSelector(selectCartItems);
  const dispatch = useDispatch();
  const hasInvalidStockQuantity = useSelector(
  selectHasInvalidStockQuantity,
);
  const navigate = useNavigate();
  const location = useLocation();
  const { bundles: cartBundles, standaloneItems } = useMemo(() => groupCartItems(cartItems),
    [cartItems],
  );

  const [authTick, setAuthTick] = useState(0);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState("login");
  const [rootPersonalizationOpen, setRootPersonalizationOpen] = useState(false);
  const [homeProfileFlowOpen, setHomeProfileFlowOpen] = useState(false);
  const [address, setAddress] = useState("");

  const authToken = useMemo(() => {
    return localStorage.getItem("authToken");
  }, [authTick]);

  const isAuthenticated = Boolean(authToken);
  const hasPendingSurveyToken = Boolean(getPendingSurveyToken());

  const openLoginModal = () => {
    setAuthModalMode("login");
    setAuthModalOpen(true);
  };

  const {
    data,
    isSuccess: isProfileSuccess,
    isError: isProfileError,
    error,
    isLoading: isProfileLoading,
    isFetching: isProfileFetching,
    refetch: refetchProfile,
  } = useProfileReadQuery(undefined, {
    skip: !isAuthenticated,
  });

  const profileMissing =
    isAuthenticated &&
    isProfileError &&
    (error?.status === 404 || error?.originalStatus === 404);

  const hasCompletedSurvey =
    isAuthenticated && isProfileSuccess && Boolean(data);

  const isProfileChecking =
    isAuthenticated && (isProfileLoading || isProfileFetching);

  const canShowCart = !isAuthenticated ||  hasCompletedSurvey;

  useEffect(() => {
    const handleAuthChanged = () => {
      setAuthTick((prev) => prev + 1);
    };

    window.addEventListener("eveness-auth-changed", handleAuthChanged);

    return () => {
      window.removeEventListener("eveness-auth-changed", handleAuthChanged);
    };
  }, []);

  useEffect(() => {
    if ( !isAuthenticated && location.state?.authRequired ) {
      openLoginModal();
      navigate(location.pathname, {
        replace: true,
        state: null,
      });
    }
  }, [
    isAuthenticated,
    location.pathname,
    location.state,
    navigate,
  ]);

  useEffect(() => {
    if (!isAuthenticated || isProfileChecking) return;
    if (profileMissing && !hasPendingSurveyToken) {
      setRootPersonalizationOpen(true);
      return;
    }
    setRootPersonalizationOpen(false);
  }, [
    isAuthenticated,
    isProfileChecking,
    profileMissing,
    hasPendingSurveyToken,
  ]);

  useEffect(() => {
    if (data) {
      const fullAddress = [
        data.address_line1,
        data.address_line2,
        data.city,
        data.state,
        data.country,
        data.postal_code,
      ]
        .filter(Boolean)
        .filter((part) => String(part).trim())
        .join(", ");

      setAddress(fullAddress);
    }

    if (error && !profileMissing) {
      console.error("Error fetching profile:", error);
    }
  }, [data, error, profileMissing]);

const orderTotals = useMemo(() => {
  const { bundles, standaloneItems } = groupCartItems(cartItems);
  const totals = { subtotal: 0, discount: 0, finalSubtotal: 0};

  standaloneItems.forEach((item) => {
    const product = item?.product || {};
    const quantity = Number(item?.quantity || 1);

    const pricing = getProductPricing(
      product,
      isCollectionCartProduct(product),
    );

    const original =
      pricing.originalPrice * quantity;

    const final =
      pricing.discountedPrice * quantity;

    totals.subtotal += original;
    totals.discount += Math.max(
      original - final,
      0,
    );
    totals.finalSubtotal += final;
  });

  bundles.forEach((bundle) => {
    const pricing = getCartBundlePricing(bundle);
    totals.subtotal += pricing.originalSubtotal;
    totals.discount += pricing.totalSavings;
    totals.finalSubtotal += pricing.total;
  });

  return totals;
}, [cartItems]);

const subtotal = orderTotals.subtotal;
const discount = orderTotals.discount;
const discountedSubtotal = orderTotals.finalSubtotal;

  const itemCount = useMemo(() => {
    return cartItems.reduce(
      (sum, item) => sum + Number(item?.quantity || 1),
      0,
    );
  }, [cartItems]);

  const shipping = 0;
  const total = discountedSubtotal + shipping;
  const quickPayItems = useMemo(
    () =>
      standaloneItems.map((item) => ({
        variant_id: Number(
          item?.product?.variant_id ||
          item?.product?.id,
        ),
        quantity: Number(item?.quantity || 1),
      })),
    [standaloneItems],
  );

  const quickPayBundles = useMemo(() =>
      cartBundles
        .map((bundle) => {
          const bundleId = Number(bundle?.id);

          const variantIds = (bundle?.items || [])
            .map((item) => Number(item?.product?.variant_id || item?.product?.id))
            .filter((id) => Number.isFinite(id) && id > 0);

          if (!Number.isFinite(bundleId) || bundleId <= 0 ||
            variantIds.length !== (bundle?.items || []).length) {
            return null;
          }

          return {
            bundle_id: bundleId,
            quantity: Math.max(1, Number(bundle?.quantity || 1)),
            variant_ids: variantIds,
          };
        })
        .filter(Boolean),
    [cartBundles],
  );

  const quickPayAmount = useMemo(() => Math.round(Number(total) * 100), [total]);

  const handleViewProduct = (product) => {
    const productSlug =
      product?.real_slug ||
      product?.slug ||
      product?.productSlug ||
      product?.product_url ||
      "";

    if (!productSlug) {
      toast.error("Product slug is not available.");
      return;
    }

    const productType =
      product?.productType ||
      product?.product_type ||
      (String(productSlug).startsWith("mrk-") ? "marketplace" : "product");

    navigate(`/product/${productSlug}`, {
      state: {
        productId: product?.product_id || product?.id,
        slug: productSlug,
        productSlug,
        productType,
        product_type: productType,
        is_marketplace: productType === "marketplace",
        matchPercentage:
          product?.matchPercentage ??
          product?.match_percentage ??
          product?.matchScore ??
          null,
      },
    });
  };

  const handleConfirmAddress = () => {
    if (!cartItems.length) {
      toast.error("Your cart is empty.");
      return;
    }

    if (hasInvalidStockQuantity) {
      toast.error(
        "Some cart quantities exceed the available stock. Please update your cart.",
      );
      return;
    }
    const checkoutState = { checkoutMode: "cart" };

    // Guest checkout
    if (!isAuthenticated) {
      navigate("/order-information", { state: checkoutState });
      return;
    }

    if (!hasCompletedSurvey) {
      if (hasPendingSurveyToken) {
        toast(
          "Your profile is being updated. Please wait a moment.",
        );
        refetchProfile?.();
        return;
      }
      toast.error("Please complete your survey before checkout.",);
      setRootPersonalizationOpen(true);
      return;
    }

    navigate("/shipping-address", { state: checkoutState });
  };

  const handleHomeProfileComplete = async () => {
    setHomeProfileFlowOpen(false);
    setRootPersonalizationOpen(false);
    setAuthTick((prev) => prev + 1);
    await refetchProfile?.();
    window.dispatchEvent(new Event("eveness-auth-changed"));
  };

  return (
    <div className="min-h-screen bg-[#FAFAFB]">
      <SEO
        title="Cart Summary | Review Your Eveness Order"
        description="Review your Eveness cart, selected products, sizes, quantities, order summary, estimated tax, and checkout details."
        path="/cart"
      />
      <NewNavbar bottomNav="cart" />
      <AuthModal
        isOpen={authModalOpen}
        initialMode={authModalMode}
        onClose={() => setAuthModalOpen(false)}
      />

      <RootPersonalizationModal
        isOpen={
          rootPersonalizationOpen &&
          !hasPendingSurveyToken &&
          (!isAuthenticated || profileMissing)
        }
        variant={isAuthenticated ? "survey" : "guest"}
        onClose={() => setRootPersonalizationOpen(false)}
        onSignIn={() => {
          setRootPersonalizationOpen(false);
          openLoginModal();
        }}
        onUnlockMatch={() => {
          setRootPersonalizationOpen(false);
          if (!isAuthenticated) {
            openLoginModal();
            return;
          }

          setHomeProfileFlowOpen(true);
        }}
      />

      <HomeProfilePopupFlow
        isOpen={homeProfileFlowOpen}
        mode="restart"
        onClose={() => setHomeProfileFlowOpen(false)}
        onRestartComplete={handleHomeProfileComplete}
        isAuthenticated={isAuthenticated}
      />

      <div className="w-full max-w-[1480px] px-4 pb-16 pt-[40px] sm:px-6 lg:px-8">
        <div className="pb-4">
          <div className="text-[36px] font-bold leading-[34px] text-black">
            CART SUMMARY
          </div>
          <p className="mt-1 text-[14px] leading-[18px] text-[#7B7B87]">
            {`${itemCount} ${itemCount === 1 ? "item" : "items"}`}
          </p>
        </div>
        {isAuthenticated && isProfileChecking ? (
          <AccessMessage>Checking your profile...</AccessMessage>
        ) : isAuthenticated && profileMissing ? (
          <AccessMessage>
            Complete your personalization survey to use your cart.
          </AccessMessage>
        ) : null}
        {canShowCart ? (
          <div className="mt-5 grid gap-3 lg:grid-cols-[1fr_400px]">
            <div className="space-y-3">
              {cartItems.length === 0 ? (
                <EmptyCartState />
              ) : (
                <>
                  {cartBundles.map((bundle) => (
                    <CartBundleCard
                      key={bundle.instanceId || bundle.id}
                      bundle={bundle}
                      showRemove
                      onViewProduct={handleViewProduct}
                      onRemove={() => {
                        dispatch(removeBundleFromCart(bundle.instanceId || bundle.id));
                        toast.success("Bundle removed from cart.");
                      }}
                    />
                  ))}
                  {standaloneItems.map((item) => (
                    <CartRow
                      key={`${item?.product?.id}-${item?.product?.size || ""}`}
                      item={item}
                      onViewProduct={handleViewProduct}
                    />
                  ))}
                </>
              )}
            </div>

            <aside className="rounded-[6px] border border-[#D4D4D4] bg-white p-10">
              <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.35em] text-[#8B93A3]">
                &gt; Module: Color_Palette
              </p>

              <h2 className="text-[34px] font-bold uppercase leading-[38px] text-black">
                Order Summary
              </h2>

              <div className="mt-4 rounded-[5px] border border-[#E2E2E2] bg-white p-4">
                <div className="flex justify-between text-[12px] uppercase tracking-[0.12em] text-[#7D8595]">
                  <span>Subtotal</span>
                  <span>{formatStorePrice(subtotal)}</span>
                </div>
                <div className="mt-4 flex justify-between text-[12px] uppercase tracking-[0.12em] text-[#257A45]">
                  <span>Discount</span>
                  <span>
                    {discount > 0
                      ? `-${formatStorePrice(discount)}`
                      : formatStorePrice(0)}
                  </span>
                </div>

                <div className="mt-4 flex justify-between text-[12px] uppercase tracking-[0.12em] text-[#7D8595]">
                  <span>Shipping Standard</span>
                  <span>{formatStorePrice(shipping)}</span>
                </div>
              </div>
              <div className="mt-3 flex justify-between rounded-[5px] border border-[#E2E2E2] bg-white px-4 py-3 text-[13px] font-bold uppercase tracking-[0.12em] text-[#7D8595]">
                <span>Total</span>
                <span>{formatStorePrice(total)}</span>
              </div>

              <button
                type="button"
                onClick={handleConfirmAddress}
                disabled={
                  !cartItems.length ||
                  hasInvalidStockQuantity
                }
                className="mt-3 h-[40px] w-full rounded-[4px] bg-[#252525] text-[14px] text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {hasInvalidStockQuantity
                  ? "Update Cart Quantities"
                  : "Checkout"}
              </button>
              {isAuthenticated && cartItems.length ? (
                <div className="mt-2">
                  <p className="text-center font-helvena text-[14px] font-semibold text-black">Express pay</p>
                  <div className="mt-2 grid grid-cols-1 gap-[8px]">
                    {/* Apple Pay */}
                    <QuickPayButton
                      express
                      className="w-full min-w-0"
                      items={quickPayItems}
                      bundles={quickPayBundles}
                      amount={quickPayAmount}
                      disabled={!cartItems.length || hasInvalidStockQuantity}
                      buttonHeight={45}
                      buttonRadius={5}
                      applePayTheme="black"
                    />
                  </div>
                </div>
              ) : null}
            </aside>
          </div>
        ) : null}{" "}
      </div>
       <Footer />
    </div>
  );
};

export default NewCartPage;
